export const ensureArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeWonHandoffString = (value) => String(value || "").trim();

export const normalizeWonHandoffMetaLabel = (value) =>
  normalizeWonHandoffString(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const resolveWonHandoffBillingMetaLabel = (productType, billingPeriod) => {
  const normalizedBillingPeriod = normalizeWonHandoffMetaLabel(billingPeriod);
  if (!normalizedBillingPeriod) return "";
  const normalizedProductType = normalizeWonHandoffString(productType).toLowerCase();
  if (normalizedProductType !== "subscription") return "";
  return normalizedBillingPeriod;
};

export const toSqlDate = (value = new Date()) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

export const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const isWonHandoffContractAppendMode = (mode) =>
  normalizeWonHandoffString(mode).toLowerCase() === "contract_append";

export const buildContractAppendExclusions = (items = []) => ({
  lineItemIds: new Set(
    ensureArray(items)
      .map((item) => parsePositiveId(item?.deal_line_item_id))
      .filter(Boolean)
  ),
  productIds: new Set(
    ensureArray(items)
      .map((item) => parsePositiveId(item?.product_id))
      .filter(Boolean)
  ),
});

export const buildWonHandoffDialogCopy = ({ t, isContractAppendMode, deal, contract }) => {
  const translate = typeof t === "function" ? t : (key) => key;
  if (isContractAppendMode) {
    return {
      title: translate("ncrm.contracts.details.addProductDialog.title"),
      subtitle: translate("ncrm.contracts.details.addProductDialog.hint", { contract }),
      submitLabel: translate("ncrm.contracts.details.addProductDialog.actions.submit"),
      productsTitle: translate("ncrm.contracts.details.addProductDialog.products.title"),
      productsHint: translate("ncrm.contracts.details.addProductDialog.products.hint"),
      productsEmpty: translate("ncrm.contracts.details.addProductDialog.products.empty"),
    };
  }
  return {
    title: translate("ncrm.deals.board.wonHandoff.title"),
    subtitle: translate("ncrm.deals.board.wonHandoff.hint", { deal }),
    submitLabel: translate("ncrm.deals.board.wonHandoff.actions.submit"),
    productsTitle: translate("ncrm.deals.board.wonHandoff.products.title"),
    productsHint: translate("ncrm.deals.board.wonHandoff.products.hint"),
    productsEmpty: translate("ncrm.deals.board.wonHandoff.products.empty"),
  };
};

export const buildAvailableWonHandoffProductOptions = ({
  form,
  lineItems,
  productOptions,
  eligibleProductOptions,
  excludedProductIds,
} = {}) => {
  const selectedLineItemIds = new Set(
    ensureArray(form?.selected_line_item_ids).map((value) => parsePositiveId(value)).filter(Boolean)
  );
  const selectedLineItemProductIds = new Set(
    ensureArray(lineItems)
      .filter((item) => selectedLineItemIds.has(parsePositiveId(item?.id)))
      .map((item) => parsePositiveId(item?.product_id))
      .filter(Boolean)
  );
  const remainingLineItemProductIds = new Set(
    ensureArray(lineItems)
      .filter((item) => {
        const lineItemId = parsePositiveId(item?.id);
        return Boolean(lineItemId && !selectedLineItemIds.has(lineItemId) && parsePositiveId(item?.product_id));
      })
      .map((item) => parsePositiveId(item?.product_id))
      .filter(Boolean)
  );
  const attachedProductIds = new Set(
    ensureArray(form?.product_ids_to_attach).map((value) => parsePositiveId(value)).filter(Boolean)
  );
  const excludedIds = excludedProductIds instanceof Set ? excludedProductIds : new Set();
  const optionSource = ensureArray(productOptions).length ? ensureArray(productOptions) : ensureArray(eligibleProductOptions);
  return optionSource.filter((item) => {
    const productId = parsePositiveId(item?.id);
    if (!productId || attachedProductIds.has(productId)) return false;
    if (remainingLineItemProductIds.has(productId)) return true;
    if (excludedIds.has(productId) || selectedLineItemProductIds.has(productId)) return false;
    return true;
  });
};

export const resolveWonHandoffInitialLineItemIds = ({
  items,
  initialLineItemIds,
  isContractAppendMode,
} = {}) => {
  if (Array.isArray(initialLineItemIds)) {
    return ensureArray(initialLineItemIds).map((value) => parsePositiveId(value)).filter(Boolean);
  }
  if (isContractAppendMode) return [];
  return ensureArray(items)
    .filter((item) => parsePositiveId(item?.id) && parsePositiveId(item?.product_id))
    .map((item) => parsePositiveId(item?.id))
    .filter(Boolean);
};

export const buildWonHandoffSubmitRequest = ({
  isContractAppendMode,
  contractId,
  dealId,
  targetStageId,
  contractNumber,
  contractDate,
  reason,
  selectedLineItemIds,
  productIdsToAttach,
  dealPatch,
} = {}) => {
  const payload = {
    reason,
    deal_line_item_ids: ensureArray(selectedLineItemIds),
    product_ids_to_attach: ensureArray(productIdsToAttach),
  };
  if (isContractAppendMode) {
    return {
      url: `/ncrm/contracts/${parsePositiveId(contractId)}/items/from-deal-flow`,
      payload,
    };
  }
  payload.deal_id = parsePositiveId(dealId);
  payload.contract_number = contractNumber;
  payload.contract_date = contractDate;
  if (dealPatch) payload.deal_patch = dealPatch;
  if (targetStageId) payload.stage_id = targetStageId;
  return { url: "/ncrm/contracts/handoff", payload };
};

export const DEFAULT_SELECT_MENU_PROPS = Object.freeze({
  maxHeight: 320,
  contentClass: "ncrm-deal-won-handoff__select-menu",
  zIndex: 2710,
});
